/**
 * One-shot sound effects, on their own bus.
 *
 * Effects are **feedback on the player's input**, not notes in the chart. That
 * distinction decides almost everything here: they are never scheduled against
 * `MusicClock`, because the thing they are confirming already happened and any
 * delay added on the way out is felt as input lag. They start at "now" and take
 * whatever the output latency is.
 *
 * ## Logical names, not file names
 *
 * Callers ask for `play('noteHit')`. Nothing outside the manifest knows what
 * file that is, which is what lets the sound be swapped by dropping a different
 * WAV in place — no rebuild, no code change, nothing to recompile.
 *
 * ## Optional by design
 *
 * A missing or undecodable effect warns once and disables that one sound. It
 * never fails the load, because a game that will not start because a 57 KB
 * courtesy sound is absent is worse than a game that is quiet.
 *
 * ## The file first, the built-in second
 *
 * "Quiet" is still the wrong answer for the effect that confirms a hit, and on
 * the public build no audio is bundled at all, so the file is *always* missing
 * there. When one cannot be used the effect is synthesised instead (see
 * `SfxFallback`), which is why the fetch failure is recorded even when a sound
 * ends up playing: the file failed, and the report says so. The order is what
 * keeps this an escape hatch rather than a policy — a WAV that fetches and
 * decodes is used, and the built-in is never consulted.
 */

import { DEFAULT_SFX_FALLBACKS, type SfxFallbackMap } from './SfxFallback.js';

/** Logical effect name to URL. Swapping the file behind a name is the point. */
export type SfxManifest = Readonly<Record<string, string>>;

export const DEFAULT_SFX_MANIFEST: SfxManifest = Object.freeze({
  noteHit: '/audio/sfx/note-hit.wav',
});

export interface SfxPlayerOptions {
  readonly context: BaseAudioContext;
  /** Where effects are mixed. The engine's own SFX gain, normally. */
  readonly destination: AudioNode;
  readonly manifest?: SfxManifest;
  /** Injected so tests need neither a network nor a decoder. */
  readonly fetchAudio?: (url: string) => Promise<ArrayBuffer>;
  /**
   * Built-in effects, used only where the file could not be. Pass `{}` to keep
   * the old behaviour of going quiet instead.
   */
  readonly fallbacks?: SfxFallbackMap;
}

export interface SfxReport {
  readonly names: readonly string[];
  readonly loaded: readonly string[];
  /** Loaded names that are sounding the built-in effect, not the file. */
  readonly fallbacks: readonly string[];
  readonly failed: readonly { readonly name: string; readonly url: string; readonly reason: string }[];
  /** One-shots started since construction. */
  readonly played: number;
  /** Peak sample magnitude per loaded effect, for setting a sane level. */
  readonly peaks: Readonly<Record<string, number>>;
}

export class SfxPlayer {
  private readonly context: BaseAudioContext;
  private readonly destination: AudioNode;
  private readonly manifest: SfxManifest;
  private readonly fetchAudio: (url: string) => Promise<ArrayBuffer>;
  private readonly fallbacks: SfxFallbackMap;
  /** Decoded once, reused for every hit. Decoding per tap would be absurd. */
  private readonly buffers = new Map<string, AudioBuffer>();
  private readonly peaks = new Map<string, number>();
  private readonly synthesised = new Set<string>();
  private readonly failures: { name: string; url: string; reason: string }[] = [];
  private playCount = 0;
  private readonly active = new Map<AudioBufferSourceNode, () => void>();
  private disposed = false;

  constructor(options: SfxPlayerOptions) {
    this.context = options.context;
    this.destination = options.destination;
    this.manifest = options.manifest ?? DEFAULT_SFX_MANIFEST;
    this.fallbacks = options.fallbacks ?? DEFAULT_SFX_FALLBACKS;
    this.fetchAudio =
      options.fetchAudio ??
      (async (url) => {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
        return response.arrayBuffer();
      });
  }

  /**
   * Fetches and decodes every effect in the manifest.
   *
   * Never rejects. Each name is independent, so one missing file costs that one
   * sound and nothing else — and where a built-in exists, it costs nothing at
   * all but the file's own character.
   *
   * Note what counts as "unusable": not only a 404. A host that answers an
   * unknown path with its index page — which is what a static SPA host does,
   * and what the public deployment does — returns 200 with HTML, so `ok` is
   * true and the failure surfaces one step later, in the decoder. Both arrive
   * here, and both take the same route.
   */
  async load(): Promise<void> {
    await Promise.all(
      Object.entries(this.manifest).map(async ([name, url]) => {
        try {
          const bytes = await this.fetchAudio(url);
          const buffer = await this.context.decodeAudioData(bytes);
          this.buffers.set(name, buffer);
          this.peaks.set(name, peakOf(buffer));
        } catch (cause) {
          const reason = cause instanceof Error ? cause.message : String(cause);
          this.failures.push({ name, url, reason });
          const substituted = this.synthesise(name);
          console.warn(
            `[SfxPlayer] "${name}" unavailable (${url}): ${reason}. ` +
              (substituted ? 'Using the built-in effect instead.' : 'Continuing silently.'),
          );
        }
      }),
    );
  }

  /** Self-contained hosts use the very same fallback sounds without a fetch. */
  loadBuiltins(): void {
    for (const name of Object.keys(this.fallbacks)) this.synthesise(name);
  }

  /**
   * Builds the built-in effect for a name, if there is one.
   *
   * Wrapped, because this runs on the failure path: a context that cannot make
   * a buffer, or a generator that throws, must cost the same as having no
   * fallback at all. Losing a sound is the worst thing that may happen here.
   */
  private synthesise(name: string): boolean {
    const build = this.fallbacks[name];
    if (build === undefined) return false;
    try {
      const buffer = build(this.context);
      this.buffers.set(name, buffer);
      this.peaks.set(name, peakOf(buffer));
      this.synthesised.add(name);
      return true;
    } catch (cause) {
      const reason = cause instanceof Error ? cause.message : String(cause);
      console.warn(`[SfxPlayer] the built-in "${name}" could not be built: ${reason}.`);
      return false;
    }
  }

  /** True once this effect can actually be played. */
  has(name: string): boolean {
    return this.buffers.has(name);
  }

  /**
   * Fires a one-shot.
   *
   * A **fresh `AudioBufferSourceNode` every time**, which is not an
   * implementation detail — a source node is single-use by specification, and
   * reusing one would mean stopping the previous hit to start the next. Under a
   * fast run of notes that turns a run of hits into a single stutter. Nodes are
   * disconnected on `ended`, so the graph does not grow.
   *
   * Silently does nothing when the effect failed to load.
   */
  play(name: string, gain = 1): boolean {
    if (this.disposed) return false;
    const buffer = this.buffers.get(name);
    if (buffer === undefined) return false;

    const source = this.context.createBufferSource();
    source.buffer = buffer;

    let node: AudioNode = source;
    if (gain !== 1) {
      const shaper = this.context.createGain();
      shaper.gain.value = gain;
      source.connect(shaper);
      node = shaper;
    }
    node.connect(this.destination);
    source.onended = () => {
      this.active.delete(source);
      source.disconnect();
      if (node !== source) node.disconnect();
    };
    this.active.set(source, () => {
      source.onended = null;
      try { source.stop(); } finally {
        source.disconnect();
        if (node !== source) node.disconnect();
      }
    });
    // No `when`: the judgement has already happened and the player is waiting
    // to hear about it. Scheduling would only add latency to a confirmation.
    source.start();
    this.playCount += 1;
    return true;
  }

  report(): SfxReport {
    return {
      names: Object.keys(this.manifest),
      loaded: [...this.buffers.keys()],
      fallbacks: [...this.synthesised],
      failed: [...this.failures],
      played: this.playCount,
      peaks: Object.fromEntries(this.peaks),
    };
  }

  dispose(): void {
    this.stopAll();
    this.disposed = true;
    this.buffers.clear();
  }

  /** End the run without throwing away the decoded effects needed on replay. */
  stopAll(): void {
    for (const stop of this.active.values()) {
      try { stop(); } catch { /* An already-ended source is harmless. */ }
    }
    this.active.clear();
  }
}

/** Largest absolute sample across all channels. Used to choose a level. */
function peakOf(buffer: AudioBuffer): number {
  let peak = 0;
  for (let channel = 0; channel < buffer.numberOfChannels; channel += 1) {
    const data = buffer.getChannelData(channel);
    for (let index = 0; index < data.length; index += 1) {
      const magnitude = Math.abs(data[index] as number);
      if (magnitude > peak) peak = magnitude;
    }
  }
  return peak;
}
